import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Formik } from 'formik'
import { isEmpty } from 'ramda'
import * as yup from 'yup'
import { useInterval } from 'react-use'
import { useDispatch, useSelector } from 'react-redux'
import { CurrencyField, CurrencyText, FormLoader, InfoBox, InfoList } from '../../../../../views/components'
import { formatCurrency } from 'utils/currency'
import { walletApiEvents, walletApiSelectors } from '../../../../api/wallet'
import styles from '../WalletTransferContent.module.scss'
import { apiStateSelectors } from '../../../../api/apiState'
import { notificationEvents } from '../../../../notificationCenter'
import { assets } from 'modules/api/wallet/constants'
import { agencyApiEvents, agencyApiSelectors } from 'modules/api/agency'
import { $TSFixMe } from 'types/ts-migrate'

interface WalletBalance {
  balance: number
  name: string
}

interface Wallet {
  walletRef: string
  /** Present when the party has a consolidated wallet; funds live there, `walletRef` is a drained NAL. */
  consolidatedWalletRef?: string
  agencyId: string
  accountId: string
  accountType: string
  partyId: string
  propertyAddress: string
  reference: string
  partyFullName?: string
}

/** What the screen labels a wallet whose funds are read from (and moved out of) its consolidated wallet. */
const displayedAccountType = (wallet: Wallet): string =>
  wallet.consolidatedWalletRef ? 'Consolidated' : wallet.accountType

export interface WalletTransferData {
  fromWallet: Wallet
  toWallet: Wallet
  fromBalance: WalletBalance
  toBalance: WalletBalance
}

interface WalletTransferConfirmationProps {
  data: WalletTransferData
}

interface TransferFormValues {
  amount: number
}

const WalletTransferConfirmation = ({ data }: WalletTransferConfirmationProps) => {
  const dispatch = useDispatch()
  const { fromWallet, toWallet, fromBalance, toBalance } = data
  const [submitted, setSubmitted] = useState(false)
  const [transferred, setTransferred] = useState(false)
  const isTransferring = useSelector(state =>
    apiStateSelectors.isLoading(state)([walletApiEvents.walletTransfer_request]),
  )
  // Same refs the lookup epic fetched balances for: the consolidated wallet when the owner has one.
  const fromRef = walletApiSelectors.spendableWalletRef(fromWallet)
  const toRef = walletApiSelectors.spendableWalletRef(toWallet)
  const newFromBalance = useSelector(state => walletApiSelectors.getGlobalBalanceAsset(state)(fromRef))
  const newToBalance = useSelector(state => walletApiSelectors.getGlobalBalanceAsset(state)(toRef))
  // `getGlobalBalanceAsset` is `null` until the balance loads and `false` for a missing wallet;
  // only an asset has a balance. Four of the six reads below used to skip the `?.`.
  const newFromBalanceAmount = newFromBalance ? newFromBalance.balance : undefined
  const newToBalanceAmount = newToBalance ? newToBalance.balance : undefined

  const getAgency = useCallback(id => dispatch(agencyApiEvents.agency_request({ id })), [dispatch])

  const fromWalletAgency: $TSFixMe = useSelector(state => agencyApiSelectors.getAgencyById(state)(fromWallet.agencyId))
  const toWalletAgency: $TSFixMe = useSelector(state => agencyApiSelectors.getAgencyById(state)(toWallet.agencyId))

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        amount: yup
          .number()
          .required('Required.')
          .test('amount', function (amount) {
            // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
            if (amount > data.fromBalance.balance) {
              return this.createError({
                path: this.path,
                message: `Can't be greater than wallet balance of ${formatCurrency(data.fromBalance.balance)}`,
              })
            }
            return true
          }),
      }),
    [data.fromBalance],
  )

  // poll balance after submission
  useInterval(
    () => {
      dispatch(walletApiEvents.balance_request({ partyId: fromRef }))
      dispatch(walletApiEvents.balance_request({ partyId: toRef }))
    },
    submitted && !isTransferring && !transferred ? 2000 : null,
  )

  useEffect(() => {
    if (fromWallet.agencyId) {
      getAgency(fromWallet.agencyId)
    }
    if (toWallet.agencyId) {
      getAgency(toWallet.agencyId)
    }
    if (newToBalanceAmount !== toBalance?.balance && newFromBalanceAmount !== fromBalance?.balance) {
      setTransferred(true)
      dispatch(
        notificationEvents.addNotification(
          {
            type: 'success',
            heading: 'Transfer successful!',
            children: (
              <p>
                Funds transferred from {fromWallet.reference} to {toWallet.reference}. Check updated balances above.
              </p>
            ),
          },
          // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 2.
          { autoDismiss: true },
        ),
      )
    }
  }, [
    toWallet.agencyId,
    fromWallet.agencyId,
    newFromBalanceAmount,
    newToBalanceAmount,
    fromBalance.balance,
    toBalance.balance,
    dispatch,
    fromWallet.reference,
    toWallet.reference,
  ])

  const handleSubmit = useCallback(
    (values: TransferFormValues) => {
      setSubmitted(true)
      const payload = {
        // A raw `@` ref passes through the backend untouched, so an owner's CW is debited directly
        // instead of their drained NAL (which the backend would redirect anyway, but the balance
        // check above is anchored on the same ref, so keep the two consistent).
        fromAccount: fromRef,
        fromParty: fromWallet.partyId,
        agency: toWallet.agencyId,
        transfers: [
          {
            toAccount: toWallet.accountId,
            toParty: toWallet.partyId,
            asset: fromBalance.name,
            amount: values.amount,
            description: `${fromWallet.reference} => ${toWallet.reference}`,
          },
        ],
      }
      dispatch(walletApiEvents.walletTransfer_request(payload))
    },
    [dispatch, fromWallet, fromRef, toWallet, fromBalance],
  )

  return (
    <div className={styles['transfer-info']}>
      <div className="rbn--row" style={{ marginBottom: '20px' }}>
        <div className="rbn--col rbn-col-md-4">
          <h3>From Wallet</h3>
          <InfoList
            items={[
              {
                label: 'Account Type',
                value: displayedAccountType(fromWallet),
              },
              {
                label: 'Contact',
                value: data.fromWallet.partyFullName || fromWallet.partyId,
              },
              {
                label: 'Lease',
                value: fromWallet.propertyAddress,
              },
              {
                label: 'Payment Reference',
                value: fromWallet.reference,
              },
              {
                label: 'Balance',
                value: transferred ? (
                  <CurrencyText>{newFromBalanceAmount ?? 0}</CurrencyText>
                ) : (
                  <CurrencyText>{fromBalance.balance}</CurrencyText>
                ),
              },
              {
                label: 'Asset',
                value: fromBalance.name,
              },
              {
                label: 'Agency ID',
                value: fromWalletAgency?.id,
              },
              {
                label: 'Agency T/A Name',
                value: fromWalletAgency?.agency.agencyDetails.trading,
              },
              {
                label: 'Agency Company Name',
                value: fromWalletAgency?.agency.agencyDetails.companyName,
              },
            ]}
          />
        </div>
        <div className="rbn--col rbn-col-md-4">
          <h3>To Wallet</h3>
          <InfoList
            items={[
              {
                label: 'Account Type',
                value: displayedAccountType(toWallet),
              },
              {
                label: 'Contact',
                value: data.toWallet.partyFullName || toWallet.partyId,
              },
              {
                label: 'Lease',
                value: toWallet.propertyAddress,
              },
              {
                label: 'Payment Reference',
                value: toWallet.reference,
              },
              {
                label: 'Balance',
                value: transferred ? (
                  <CurrencyText>{newToBalanceAmount ?? 0}</CurrencyText>
                ) : (
                  <CurrencyText>{toBalance.balance}</CurrencyText>
                ),
              },
              {
                label: 'Asset',
                value: fromBalance.name,
              },
              {
                label: 'Agency ID',
                value: toWalletAgency?.id,
              },
              {
                label: 'Agency T/A Name',
                value: toWalletAgency?.agency.agencyDetails.trading,
              },
              {
                label: 'Agency Company Name',
                value: toWalletAgency?.agency.agencyDetails.companyName,
              },
            ]}
          />
        </div>
      </div>
      {fromBalance.name === assets.dummy ? (
        <InfoBox type="error">Unable to transfer dummy funds.</InfoBox>
      ) : fromWalletAgency?.id !== toWalletAgency?.id ? (
        <InfoBox type="error">Unable to transfer between agencies.</InfoBox>
      ) : (
        !transferred && (
          <Formik<TransferFormValues>
            initialValues={{ amount: 0 }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ handleSubmit, setFieldValue, submitCount, values, errors }) => {
              const formLoaderState =
                isTransferring || (submitted && !transferred)
                  ? 'submitting'
                  : submitCount > 0 && !isEmpty(errors)
                  ? 'error'
                  : undefined

              return (
                <FormLoader
                  onSubmit={handleSubmit}
                  state={formLoaderState}
                  submittingText="Transferring funds..."
                  buttonProps={{ children: 'Transfer' }}
                >
                  <div className="rbn--row" style={{ marginBottom: '20px' }}>
                    <div className="rbn--col-md-4">
                      <CurrencyField
                        label="Amount"
                        id="amount"
                        name="amount"
                        placeholder="Enter amount"
                        onChange={({ target }: any) => setFieldValue('amount', target.value)}
                        value={values.amount}
                        error={errors.amount}
                      />
                    </div>
                    <div className="rbn--col-md-4"></div>
                    <div className="rbn--col-md-4"></div>
                  </div>
                </FormLoader>
              )
            }}
          </Formik>
        )
      )}
    </div>
  )
}

export default WalletTransferConfirmation
